import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronDown } from 'lucide-react';
import styles from './FormikCustomSelect.module.css';
import WarningIcon from '../Support/WarningIcon';
import PropTypes from 'prop-types';
import { useTealium } from '../../../tealium/useTealium';

const getError = (customFormDetails, form, field) => {
  let error = customFormDetails && customFormDetails.errors && customFormDetails.touched && customFormDetails.touched[customFormDetails.fieldName] ? customFormDetails.errors[customFormDetails.fieldName] : null;
  error = !error ? form.errors[field.name] && form.touched[field.name] ? form.errors[field.name] : null : error;
  return error;
};

const FormikCustomSelect = ({ field, form, options, placeholder, disabled, defaultValue, customFormDetails, onBlur }) => {

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownDirection, setDropdownDirection] = useState('down');
  const selectRef = useRef(null);
  const error = getError(customFormDetails, form, field);
  const { track, events } = useTealium();

  useEffect(() => {
    if (!field.value && defaultValue) {
      form.setFieldValue(field.name, field.value || defaultValue);
    }
  }, [defaultValue, field.value, form, field.name]);

  const fieldsToClear = [
    'unitNumber',
    'street',
    'building',
    'townBuilding',
    'flatBuilding',
    'officeBuilding',
    'flatUnitNumber',
    'flatStreet',
    'address',
    'estateName',
    'suburb',
    'postalCode',
    'province',
    'provinces',
    'houseUnitNumber',
    'houseStreet',
    'townUnitNumber',
    'townStreet',
    'officeUnitNumber',
    'officeStreet',
    'city'
  ];

  const selectedOption = options.find(option => option.value === field.value) || null;
  const clearBusinessAddressFields = (form) => {

    fieldsToClear.forEach(field => form.setFieldValue(field, ''));
  };

  const handleSelect = (option) => {
    if (disabled) { return; }
    if (field.name === 'buildingType' && field.value !== option.value) {
      clearBusinessAddressFields(form);
    }

    const updatedTouched = Object.fromEntries(
      Object.entries(form.touched).filter(([key]) => !fieldsToClear.includes(key))
    );

    form.setTouched({
      ...updatedTouched,
      [field.name]: true
    });
    form.setFieldValue(field.name, option.value);
    setIsOpen(false);
    track(events.formBuilder.fieldInteraction(window.location.pathname, field.name, option.label));
  };

  const toggleDropdown = () => {
    const DROPDOWN_HEIGHT_THRESHOLD = 200;

    if (disabled) { return; }
    //form.setFieldTouched(field.name, true);

    const rect = selectRef.current?.getBoundingClientRect();
    if (rect) {
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow < DROPDOWN_HEIGHT_THRESHOLD && spaceAbove > spaceBelow) {
        setDropdownDirection('up');
      } else {
        setDropdownDirection('down');
      }
    }

    setIsOpen((prev) => !prev);
  };

  const handleBlur = (e) => {
    field.onBlur(e);
    if (onBlur) {onBlur(e);}
    form.setFieldTouched(field.name, true);
    setIsOpen(false);
  };

  return (
    <div className={error ? styles.errorSelectContainer : styles.selectContainer} ref={selectRef} tabIndex={0} onBlur={handleBlur}>
      <div
        className={`${styles.selectBox} ${disabled ? styles.disabled : ''} ${error ? styles.errorBorder : ''}`}
        onClick={toggleDropdown}
      >
        <label
          className={`${styles.selectLabel} ${selectedOption ? styles.floatingLabel : ''}`}
        >
          {placeholder || 'Select an option'}
        </label>

        {selectedOption ? (
          <span className={`${styles.selectedValue} ${disabled ? styles.disabled : ''}`}>
            {selectedOption.label}
          </span>
        ) : (
          <span className={styles.placeholder} />
        )}

        {(isOpen) ? (
          <X className={styles.icon} width={21} height={21} />
        ) : (
          <ChevronDown className={styles.icon} width={21} height={21} />
        )}
      </div>

      {(
        <div
          className={`
            ${styles.dropdownContainer}
            ${isOpen && !disabled ? styles.open : ''}
            ${dropdownDirection === 'up' ? styles.up : styles.down}
        `}
        >
          {options.map((option) => (
            <div
              key={option.value}
              className={`
                ${styles.dropdownItem}
                ${disabled ? styles.disabled : ''}
                ${selectedOption && option.value === selectedOption.value ? styles.focused : ''}`}
              onClick={() => handleSelect(option)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
      {error && (
        <div className={styles.errorContainer}>
          <WarningIcon width='14px' height='14px' />
          <div className={styles.errorMessage}>{error}</div>
        </div>
      )}
    </div>
  );
};

export default FormikCustomSelect;

FormikCustomSelect.propTypes = {
  form: PropTypes.object,
  field: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.array,
  disabled: PropTypes.bool,
  defaultValue: PropTypes.string,
  customFormDetails: PropTypes.object,
  onBlur: PropTypes.func
};
